<template>
  <gg-card>
    <gg-card-text class="pa-3">
      <div class="d-flex flex-wrap align-center mb-3">
        <div class="body-1 mr-auto">
          <v-icon size="24" color="primary" class="pb-1" aria-hidden="true">mdi-console-line</v-icon>
          <span class="ml-2">Terminal</span>
        </div>
        <v-chip
          small
          label
          role="status"
          aria-live="polite"
          class="kvm-status"
          :class="'kvm-status--' + status">
          <span class="kvm-sr-only">Terminal status: </span>{{ statusChip.text }}
        </v-chip>
      </div>

      <v-alert v-if="error" type="error" border="left" dense class="mb-3">
        {{ error }}
      </v-alert>

      <v-alert v-if="server.power_state && server.power_state !== 'online'"
               type="info" border="left" dense class="mb-3">
        This server is {{ server.power_state }}. The terminal will connect automatically once it is powered on.
      </v-alert>

      <p id="kvm-terminal-instructions" class="kvm-caption text-caption mb-2">
        This is a live command-line shell on your server. Click or tab into the area
        below to type; output streams back in real time.
      </p>

      <!-- Height fills the viewport (resizeScreen). Clicking focuses the terminal. -->
      <div
        class="vds-terminal-screen"
        role="group"
        aria-label="Server terminal (serial console)"
        aria-describedby="kvm-terminal-instructions"
        :style="{ height: screenHeight + 'px' }"
        @click="focusTerminal">
        <!-- Dedicated mount for xterm — Vue must NEVER manage children here, or it
             clobbers xterm's injected DOM on re-render (e.g. status changes). -->
        <div ref="screen" class="vds-terminal-mount"></div>
        <div v-if="status !== 'connected'" class="vds-terminal-placeholder" aria-hidden="true">
          <v-progress-circular v-if="status === 'connecting'" indeterminate color="primary" class="mb-2" />
          <div>{{ placeholderText }}</div>
        </div>
      </div>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { Terminal } from 'xterm'
import { FitAddon } from 'xterm-addon-fit'
import { AttachAddon } from 'xterm-addon-attach'
import 'xterm/css/xterm.css'
import kvm from '@/api/kvm'

// Auto-reconnect: after a dropped/failed session, retry on a fixed 5s cadence
// (only while the terminal is on-screen and the server is powered on).
const RECONNECT_DELAY_MS = 5000

// While the server is powered off we don't mint sessions at all (each attempt is
// a POST /vm/console that can only fail); KvmServiceView re-reads the power state
// on this cadence instead, and we connect once it reports online.
const POWER_WAIT_INTERVAL_MS = 10000

// If a socket never opens (no onopen/onclose fires), treat it as failed after this
// long and retry — otherwise the component would sit stuck on "Connecting…".
const CONNECT_TIMEOUT_MS = 12000

// Space to leave below the terminal (card padding) so growing it to fill the
// viewport never introduces a page scrollbar. Tune if a gap/scrollbar appears.
const SCREEN_BOTTOM_RESERVE = 50
const SCREEN_MIN_HEIGHT = 320

export default {
  name: 'HostingKvmTerminal',
  props: {
    // Provided by KvmServiceView via <router-view :server :loading>.
    server: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    // KvmServiceView.watchPower — quietly re-reads the server until it reaches a
    // power state; returns a cancel function.
    onPowerChange: { type: Function, default: null },
  },
  data: () => ({
    term: null, // xterm Terminal instance (kept alive across page navigation)
    fitAddon: null,
    attachAddon: null,
    socket: null, // the live WebSocket
    // idle | waiting (server powered off) | connecting | connected | disconnected | error
    status: 'idle',
    stopPowerWait: null, // cancels KvmServiceView's power-state watch
    error: '',
    // On-screen (keep-alive activated) vs cached in the background.
    active: false,
    // Suppresses auto-reconnect (set on teardown / leaving the VM).
    intentional: false,
    reconnecting: false,
    // The entitlement id the live session belongs to (to detect VM switches).
    connectedId: null,
    reconnectTimer: null,
    connectTimeout: null, // watchdog for a socket that never opens
    // Dynamic terminal height (px) — fills the viewport down to SCREEN_BOTTOM_RESERVE.
    screenHeight: 500,
  }),
  computed: {
    // Unknown (not yet loaded) counts as on, so we still try rather than wait forever.
    poweredOff () {
      return !!this.server.power_state && this.server.power_state !== 'online'
    },
    statusChip () {
      switch (this.status) {
        case 'waiting': return { text: 'Waiting for power on', color: 'grey' }
        case 'connected': return { text: 'Connected', color: 'primary' }
        case 'connecting': return { text: this.reconnecting ? 'Reconnecting…' : 'Connecting…', color: 'warning' }
        case 'error': return { text: 'Error', color: 'red' }
        case 'disconnected': return { text: 'Disconnected', color: 'grey' }
        default: return { text: 'Not connected', color: 'grey' }
      }
    },
    placeholderText () {
      if (this.status === 'waiting') return 'The server is powered off. The terminal will connect when it starts.'
      if (this.status === 'connecting') return this.reconnecting ? 'Reconnecting to the terminal…' : 'Opening terminal session…'
      if (this.status === 'disconnected') return 'Disconnected.'
      if (this.status === 'error') return this.error || 'Terminal unavailable.'
      return 'Connecting to the terminal…'
    },
  },
  methods: {
    async connect () {
      if (!this.server.id) {
        this.error = 'No server selected.'
        this.status = 'error'
        return
      }
      // Tear down any previous socket — console tickets are single-use.
      this.clearReconnectTimer()
      this.closeSocket()
      this.intentional = false
      this.error = ''
      this.status = 'connecting'
      try {
        this.ensureTerm()
        if (!this.term) throw new Error('The terminal is not ready yet.')
        // Mint a fresh serial session immediately before connecting.
        const { websocket } = await kvm.getConsole(this.server.id, 'serial')
        if (!websocket) throw new Error('The terminal session did not include a connection URL.')

        const socket = new WebSocket(websocket)
        socket.binaryType = 'arraybuffer'
        // AttachAddon wires terminal <-> socket both ways. The proxy authenticates
        // server-side, so there is no client-side credential step.
        const attach = new AttachAddon(socket)
        this.term.loadAddon(attach)
        socket.onopen = this.onOpen
        socket.onclose = this.onClose
        socket.onerror = this.onSocketError
        this.socket = socket
        this.attachAddon = attach
        this.connectedId = this.server.id
        // Watchdog: if it never opens, tear down and retry instead of hanging.
        this.connectTimeout = setTimeout(() => {
          if (this.status === 'connecting') {
            this.error = 'Connection timed out — reconnecting…'
            this.closeSocket()
            this.maybeReconnect()
          }
        }, CONNECT_TIMEOUT_MS)
      } catch (e) {
        this.status = 'error'
        this.error = e.message || 'Could not open the terminal session.'
        this.maybeReconnect()
      }
    },
    onOpen () {
      this.clearConnectTimeout()
      this.status = 'connected'
      this.error = ''
      this.reconnecting = false
      this.$nextTick(() => {
        this.fit()
        this.focusTerminal()
      })
    },
    // Focus xterm's hidden textarea so keystrokes are captured. xterm only reads the
    // keyboard while focused; we focus on connect, on return, and on click. The
    // delayed retry covers the textarea not being ready on the first tick.
    focusTerminal () {
      if (!this.term) return
      try { this.term.focus() } catch (e) { /* not ready */ }
      setTimeout(() => { if (this.term && this.active) { try { this.term.focus() } catch (e) { /* gone */ } } }, 60)
    },
    onClose () {
      this.clearConnectTimeout()
      this.socket = null
      if (this.intentional) {
        this.status = 'disconnected'
        return
      }
      const wasConnected = this.status === 'connected'
      this.status = wasConnected ? 'error' : 'connecting'
      this.error = wasConnected ? 'Connection lost — reconnecting…' : ''
      this.maybeReconnect()
    },
    onSocketError () {
      // The browser fires onerror then onclose; let onClose drive the recovery.
      if (!this.error) this.error = ''
    },
    // Retry on a fixed 5s cadence. Only while on-screen — a drop while the terminal
    // is in the background reconnects when the user returns (activated).
    maybeReconnect () {
      this.clearReconnectTimer()
      if (this.intentional) return
      if (!this.active) {
        this.status = 'disconnected'
        return
      }
      if (this.poweredOff) {
        this.waitForPower()
        return
      }
      this.reconnecting = true
      this.status = 'connecting'
      // A failure may mean the server just went down; have the parent re-read the
      // power state once before the retry, so ensureConnected() can switch to
      // waiting instead of minting another doomed session.
      if (this.onPowerChange) this.stopPowerWait = this.onPowerChange()
      this.reconnectTimer = setTimeout(() => { this.ensureConnected() }, RECONNECT_DELAY_MS)
    },
    // Server is powered off: drop any session, stop retrying, and let the parent
    // poll the power state; the server.power_state watcher connects on 'online'.
    waitForPower () {
      this.clearReconnectTimer()
      this.closeSocket()
      this.status = 'waiting'
      this.error = ''
      this.reconnecting = false
      if (this.onPowerChange) {
        this.stopPowerWait = this.onPowerChange('online', { interval: POWER_WAIT_INTERVAL_MS, tries: Infinity })
      }
    },
    cancelPowerWait () {
      if (this.stopPowerWait) {
        this.stopPowerWait()
        this.stopPowerWait = null
      }
    },
    // Ensure a live session for the current VM. If one is already up, just refocus;
    // otherwise (idle / stuck-connecting / errored / dropped / other VM) connect
    // fresh — connect() tears down any stale socket first. Deliberately does NOT
    // early-return on 'connecting', so a stalled attempt is always recoverable by
    // re-entering the page.
    ensureConnected () {
      if (!this.server.id) return
      if (this.poweredOff) {
        this.waitForPower()
        return
      }
      if (this.status === 'connected' && this.connectedId === this.server.id) {
        this.$nextTick(this.focusTerminal)
        return
      }
      this.connect()
    },
    // Create the xterm instance once and mount it into the screen element.
    ensureTerm () {
      if (this.term) return
      const term = new Terminal({
        cursorBlink: true,
        fontFamily: 'Menlo, Consolas, "DejaVu Sans Mono", monospace',
        fontSize: 14,
        scrollback: 5000,
        theme: { background: '#000000' },
      })
      const fit = new FitAddon()
      term.loadAddon(fit)
      term.open(this.$refs.screen)
      this.term = term
      this.fitAddon = fit
    },
    fit () {
      if (this.fitAddon && this.active) {
        try { this.fitAddon.fit() } catch (e) { /* element not measurable yet */ }
      }
    },
    clearReconnectTimer () {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = null
      }
    },
    clearConnectTimeout () {
      if (this.connectTimeout) {
        clearTimeout(this.connectTimeout)
        this.connectTimeout = null
      }
    },
    // Grow the terminal to fill the space from its top down to the viewport bottom
    // (less the reserve), then refit so xterm recomputes its cols/rows.
    resizeScreen () {
      if (!this.active) return
      this.$nextTick(() => {
        const el = this.$refs.screen
        if (!el) return
        const top = el.getBoundingClientRect().top
        const avail = window.innerHeight - top - SCREEN_BOTTOM_RESERVE
        this.screenHeight = Math.max(SCREEN_MIN_HEIGHT, Math.floor(avail))
        this.$nextTick(this.fit)
      })
    },
    // Close the socket + detach the addon without surfacing a spurious error.
    closeSocket () {
      this.clearConnectTimeout()
      if (this.attachAddon) {
        try { this.attachAddon.dispose() } catch (e) { /* already gone */ }
        this.attachAddon = null
      }
      if (this.socket) {
        this.socket.onopen = null
        this.socket.onclose = null
        this.socket.onerror = null
        try { this.socket.close() } catch (e) { /* already closing */ }
        this.socket = null
      }
    },
    // Full teardown — only on leaving the VM (keep-alive destroyed).
    cleanup () {
      this.closeSocket()
      if (this.term) {
        try { this.term.dispose() } catch (e) { /* already gone */ }
        this.term = null
        this.fitAddon = null
      }
    },
  },
  // Refit when the layout above the terminal changes (alerts toggling, etc.).
  watch: {
    status () { this.resizeScreen() },
    error () { this.resizeScreen() },
    'server.power_state' (state) {
      this.resizeScreen()
      if (!this.active) return
      if (state === 'online' && this.status === 'waiting') {
        this.cancelPowerWait()
        this.connect()
      } else if (this.poweredOff && this.status !== 'connected' && this.status !== 'waiting') {
        // Went down while connecting/retrying — stop minting sessions.
        this.waitForPower()
      }
    },
  },
  // This component is NOT kept alive (see KvmServiceView) — it mounts fresh and
  // connects each time the Terminal page is opened, and tears down on leave.
  mounted () {
    this.active = true
    window.addEventListener('resize', this.resizeScreen)
    this.resizeScreen()
    this.ensureConnected()
  },
  beforeDestroy () {
    this.active = false
    this.intentional = true
    this.clearReconnectTimer()
    this.cancelPowerWait()
    this.cleanup()
    window.removeEventListener('resize', this.resizeScreen)
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

// Contrast-safe secondary text for the terminal instructions (replaces the faint
// global grey).
.kvm-caption {
  @include kvm-secondary-text;
}

.kvm-sr-only {
  @include kvm-sr-only;
}

// Status badge: text-backed (never colour-alone), AA-contrast in both themes.
// Fixes white-on-amber / white-on-green failures by owning fg+bg per state.
.kvm-status {
  font-weight: 600;
}
.kvm-status--connected {
  // #fff on #0a7c3c = 5.30:1; badge vs card 5.30:1 (light) / 3.41:1 (dark).
  background-color: #0a7c3c !important;
  color: #ffffff !important;
}
.kvm-status--connecting {
  // dark text on amber (white-on-amber fails); ~10.7:1. Border defines the
  // badge boundary on a light card (#b07400 vs #fff ≈ 3.9:1).
  background-color: #ffb70f !important;
  color: rgba(0, 0, 0, 0.87) !important;
  border: 1px solid #b07400;
}
.kvm-status--error {
  // #fff on #c62828 = 5.63:1.
  background-color: #c62828 !important;
  color: #ffffff !important;
}
.kvm-status--disconnected,
.kvm-status--waiting,
.kvm-status--idle {
  // #fff on #5b6670 = 5.87:1.
  background-color: #5b6670 !important;
  color: #ffffff !important;
}
.theme--dark {
  .kvm-status--disconnected,
  .kvm-status--waiting,
  .kvm-status--idle {
    // #fff on #6b7681 = 4.63:1; badge vs dark card ≈ 3.9:1.
    background-color: #6b7681 !important;
  }
}
.kvm-status ::v-deep .v-chip__content {
  color: inherit;
}

.vds-terminal-screen {
  position: relative;
  width: 100%;
  min-height: 320px;
  background: #000;
  border-radius: 4px;
  overflow: hidden;
  padding: 6px;
}
.vds-terminal-mount {
  width: 100%;
  height: 100%;
}
.vds-terminal-placeholder {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #cfd6dc; // ~14:1 on the black screen
  text-align: center;
  padding: 16px;
  pointer-events: none;
}
</style>
